// src/services/fix-actions-service.ts
import { apiFetch } from "../../../api/api-fetch";
import type { StrategyArtifact } from "./optimization-studio-service";

// ============================================================================
// TYPES
// ============================================================================

export type FixActionPriority = "HIGH" | "MEDIUM" | "LOW" | string;
export type FixActionStatus = "open" | "in-progress" | "completed" | "archived" | string;
export type ActionEventStatus = "Open" | "In-Progress" | "Closed" | string;

export interface FixActionArtifact {
  id: string;
  title: string;
  type: string;
  description: string;
  expectedImpact?: string;
  deployment?: string[];
  generatedArtifactId?: string;
  generatedStatus?: string;
}

export interface FixAction {
  id: number;
  title: string;
  description: string;
  category: string;
  strategicTheme?: string;
  priority: FixActionPriority;
  effort: string;
  impactLevel: string;
  status?: FixActionStatus;
  createdAt: string;
  impactScore?: number;
  observation?: string;
  strategicGoal?: string;
  reasoningInfluence?: string;
  expectedOutcome?: string;
  artifacts?: FixActionArtifact[];
  sourceModule?: string;
  sourceId?: string;
  stageId?: string;
}

export type TemplateCategory = "schema" | "content" | "data" | "all" | string;

export interface ContentTemplate {
  id: string;
  name: string;
  description: string;
  category: TemplateCategory;
  iconKey: string;
  inputs: string[];
}

export interface FixActionsPageData {
  actionLog: FixAction[];
  contentTemplates: ContentTemplate[];
  context: {
    brandId: string;
    [key: string]: unknown;
  };
}

export type SuggestedSourcePayload = {
  brandName: string;
  selectedModels: string[];
  monitoringRecommendedActions: any[];
  cognitionPriorityOptimizations: any[];
  brandId: string;
};

export type SuggestedActionsResponse = {
  fixActions: FixAction[];
};

export type GenerateAssetRequest = {
  actionId: number;
  actionTitle: string;
  actionTheme: string;
  artifactTitle: string;
  artifactType: string;
  artifactDescription: string;
  config: unknown;
  brandId: string;
  /** The brand's name: the asset is written from its client's brand record. */
  brandName?: string;
};

export interface ApproveGeneratedAssetRequest {
  artifactId: string;
  brandId: string;
  /** Lets an asset made before the claim check existed be checked on approve. */
  brandName?: string;
}

export interface ImproveGeneratedAssetRequest {
  artifactId: string;
  improvement: string;
  customNote?: string;
  brandId: string;
}

export interface RegenerateGeneratedAssetRequest {
  artifactId: string;
  brandId: string;
}

export interface IngestActionLogRequest {
  sourceModule: "antenna" | "cognition" | "suggested" | "competitive-intelligence";
  sourceId?: string;
  title: string;
  description: string;
  priority?: string;
  effort?: string;
  impactScore?: number;
  impactLevel?: string;
  stageId?: string;
  brand?: string;
  category?: string;
  competitors?: string[];
  modelsUsed?: string[];
  metadata?: Record<string, unknown>;
  /** Phase 12e: how the row came to exist, e.g. "competitive-suggested". */
  origin?: string;
  brandId: string;
  autoEnrich?: boolean;
}

export interface IngestActionLogResponse {
  action: FixAction;
  deduped: boolean;
  context: { brandId: string; [k: string]: unknown };
}

// ── Backfill enrichment (NEW) ───────────────────────────────
export interface BackfillEnrichmentRequest {
  brandId: string;
  brandName?: string;
  selectedModels?: string[];
  limit?: number;
}

export interface BackfillEnrichmentResponse {
  ok: boolean;
  enrichedCount: number;
  checked: number;
  enrichedIds: number[];
}

// ── Deploy log ──────────────────────────────────────────────
export type DeployStatus = "Draft" | "In Review" | "Ready" | "Published";

export interface DeployLogEntry {
  id: number;
  artifactId: string;
  actionId: number;
  artifactName: string;
  artifactType: string;
  status: DeployStatus;
  createdAt: string;
  updatedAt: string;
}

export interface DeployLogListResponse {
  items: DeployLogEntry[];
}

export interface ReadStatusResponse {
  readActionIds: number[];
}

export interface ActionEventStatusItem {
  actionId: number;
  totalArtifacts: number;
  generatedCount: number;
  closedCount: number;
  eventStatus: ActionEventStatus;
}

export interface EventStatusResponse {
  statuses: ActionEventStatusItem[];
}

export interface UpdateEventStatusRequest {
  actionId: number;
  totalArtifacts: number;
  generatedCount: number;
  closedCount: number;
  brandId: string;
}

export interface ActionBySourceItem {
  id: number;
  title: string;
  eventStatus: ActionEventStatus;
  totalArtifacts: number;
  generatedCount: number;
  closedCount: number;
}

export interface ActionsBySourceResponse {
  byId: Record<string, ActionBySourceItem>;
}

// ============================================================================
// ARTIFACT TRACKER (unchanged)
// ============================================================================

export interface TrackedArtifact {
  artifactId: string;
  actionId: number;
  artifactIndex: number;
  artifactTitle: string;
  artifactType: string;
  status: string;
  generatedAt: string;
}

const _trackerById = new Map<string, TrackedArtifact>();
const _slotLookup = new Map<string, string>();

export const artifactTracker = {
  getAll(): TrackedArtifact[] {
    return Array.from(_trackerById.values());
  },

  getForAction(actionId: number): TrackedArtifact[] {
    return this.getAll().filter((x) => x.actionId === actionId);
  },

  getForArtifactSlot(actionId: number, artifactIndex: number): TrackedArtifact | null {
    const artifactId = _slotLookup.get(`${actionId}-${artifactIndex}`);
    if (!artifactId) return null;
    return _trackerById.get(artifactId) ?? null;
  },

  track(item: TrackedArtifact) {
    _trackerById.set(item.artifactId, item);
    _slotLookup.set(`${item.actionId}-${item.artifactIndex}`, item.artifactId);
  },

  updateStatus(artifactId: string, status: string) {
    const existing = _trackerById.get(artifactId);
    if (existing) {
      _trackerById.set(artifactId, { ...existing, status });
    }
  },

  areAllGenerated(actionId: number, totalArtifacts: number): boolean {
    if (totalArtifacts === 0) return false;
    return this.getForAction(actionId).length >= totalArtifacts;
  },

  countClosed(actionId: number): number {
    return this.getForAction(actionId).filter(
      (x) => x.status.toLowerCase() === "published"
    ).length;
  },

  hydrateFromArtifacts(artifacts: StrategyArtifact[], actions: FixAction[]): void {
    _trackerById.clear();
    _slotLookup.clear();

    const byAction = new Map<number, StrategyArtifact[]>();
    for (const a of artifacts) {
      if (!byAction.has(a.supportingActionId)) {
        byAction.set(a.supportingActionId, []);
      }
      byAction.get(a.supportingActionId)!.push(a);
    }

    for (const [actionId, actionArtifacts] of byAction.entries()) {
      const action = actions.find((a) => a.id === actionId);

      const sorted = [...actionArtifacts].sort(
        (a, b) =>
          new Date(a.generatedAt || 0).getTime() -
          new Date(b.generatedAt || 0).getTime()
      );

      const assignedSlots = new Set<number>();

      for (const artifact of sorted) {
        let slotIndex = -1;

        if (
          typeof artifact.slotIndex === "number" &&
          artifact.slotIndex >= 0 &&
          !assignedSlots.has(artifact.slotIndex)
        ) {
          slotIndex = artifact.slotIndex;
        }

        if (slotIndex < 0 && action?.artifacts && artifact.slotTitle) {
          const needle = artifact.slotTitle.trim().toLowerCase();
          const idx = action.artifacts.findIndex(
            (s) => s.title.trim().toLowerCase() === needle
          );
          if (idx >= 0 && !assignedSlots.has(idx)) {
            slotIndex = idx;
          }
        }

        if (slotIndex < 0 && action?.artifacts) {
          const needle = artifact.name.trim().toLowerCase();
          const idx = action.artifacts.findIndex(
            (s) => s.title.trim().toLowerCase() === needle
          );
          if (idx >= 0 && !assignedSlots.has(idx)) {
            slotIndex = idx;
          }
        }

        if (slotIndex < 0) {
          let next = 0;
          while (assignedSlots.has(next)) next++;
          slotIndex = next;
        }

        assignedSlots.add(slotIndex);

        const tracked: TrackedArtifact = {
          artifactId: artifact.id,
          actionId,
          artifactIndex: slotIndex,
          artifactTitle: artifact.name,
          artifactType: String(artifact.type),
          status: artifact.status,
          generatedAt:
            artifact.lastUpdated || artifact.generatedAt || new Date().toISOString(),
        };

        _trackerById.set(artifact.id, tracked);
        _slotLookup.set(`${actionId}-${slotIndex}`, artifact.id);
      }
    }
  },
};

// ============================================================================
// INTERNAL HELPERS
// ============================================================================

function jsonOptions(method: string, body: unknown) {
  return {
    method,
    headers: { "Content-Type": "application/json" } as Record<string, string>,
    body: JSON.stringify(body),
  };
}

function withQuery(path: string, query: Record<string, string | undefined>) {
  const usp = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== "") usp.set(k, String(v));
  });
  const qs = usp.toString();
  return qs ? `${path}${path.includes("?") ? "&" : "?"}${qs}` : path;
}

// ============================================================================
// SERVICE
// ============================================================================

export const fixActionsService = {
  async getFixActionsPageData(args: { brandId: string }): Promise<FixActionsPageData> {
    const url = withQuery("/fix-actions/page-data", { brandId: args.brandId });
    return apiFetch<FixActionsPageData>(url);
  },

  async ingestActionLog(req: IngestActionLogRequest): Promise<IngestActionLogResponse> {
    return apiFetch<IngestActionLogResponse>(
      "/fix-actions/action-log/ingest",
      jsonOptions("POST", req)
    );
  },

  /**
   * NEW — Ask the backend to enrich any ingested Antenna/Cognition rows that
   * are missing detail fields (observation, strategicGoal, artifacts, etc).
   *
   * Safe to call once per page load. Only acts on rows that currently need it;
   * returns `enrichedCount: 0` when everything is already enriched.
   */
  async backfillEnrichment(
    req: BackfillEnrichmentRequest
  ): Promise<BackfillEnrichmentResponse> {
    return apiFetch<BackfillEnrichmentResponse>(
      "/fix-actions/action-log/backfill-enrichment",
      jsonOptions("POST", req)
    );
  },

  async getActionsBySource(args: {
    brandId: string;
    sourceModule: "antenna" | "cognition" | "suggested" | "competitive-intelligence";
    sourceIds: string[];
  }): Promise<ActionsBySourceResponse> {
    if (!args.sourceIds?.length) return { byId: {} };
    const url = withQuery("/fix-actions/by-source", {
      brandId: args.brandId,
      sourceModule: args.sourceModule,
      sourceIds: args.sourceIds.join(","),
    });
    return apiFetch<ActionsBySourceResponse>(url);
  },

  async buildSuggestedActions(
    payload: SuggestedSourcePayload
  ): Promise<SuggestedActionsResponse> {
    return apiFetch<SuggestedActionsResponse>(
      "/fix-actions/suggested/build",
      jsonOptions("POST", payload)
    );
  },

  async generateAsset(req: GenerateAssetRequest): Promise<StrategyArtifact> {
    return apiFetch<StrategyArtifact>(
      "/fix-actions/assets/generate",
      jsonOptions("POST", req)
    );
  },

  async approveGeneratedAsset(
    req: ApproveGeneratedAssetRequest
  ): Promise<{ ok: true; artifactId: string; deployStatus: string }> {
    const url = withQuery(
      `/fix-actions/assets/${encodeURIComponent(req.artifactId)}/approve`,
      { brandId: req.brandId, brandName: req.brandName }
    );
    return apiFetch(url, jsonOptions("POST", {}));
  },

  async improveGeneratedAsset(
    req: ImproveGeneratedAssetRequest
  ): Promise<StrategyArtifact> {
    const url = withQuery(
      `/fix-actions/assets/${encodeURIComponent(req.artifactId)}/improve`,
      { brandId: req.brandId }
    );
    return apiFetch<StrategyArtifact>(
      url,
      jsonOptions("POST", {
        improvement: req.improvement,
        customNote: req.customNote,
      })
    );
  },

  async regenerateGeneratedAsset(
    req: RegenerateGeneratedAssetRequest
  ): Promise<StrategyArtifact> {
    const url = withQuery(
      `/fix-actions/assets/${encodeURIComponent(req.artifactId)}/regenerate`,
      { brandId: req.brandId }
    );
    return apiFetch<StrategyArtifact>(url, jsonOptions("POST", {}));
  },

  async getDeployLog(args: { brandId: string }): Promise<DeployLogListResponse> {
    const url = withQuery("/fix-actions/deploy-log", { brandId: args.brandId });
    return apiFetch<DeployLogListResponse>(url);
  },

  async patchDeployLogStatus(args: {
    id: number;
    status: DeployStatus;
    brandId: string;
  }): Promise<{ ok: boolean; id: number; status: string }> {
    const url = withQuery(`/fix-actions/deploy-log/${args.id}/status`, {
      brandId: args.brandId,
    });
    return apiFetch(url, jsonOptions("PATCH", { status: args.status }));
  },

  async markActionsRead(args: {
    actionIds: number[];
    brandId: string;
  }): Promise<{ ok: boolean; markedCount: number }> {
    return apiFetch(
      "/fix-actions/read-status/mark",
      jsonOptions("POST", {
        actionIds: args.actionIds,
        brandId: args.brandId,
      })
    );
  },

  async getReadStatus(args: { brandId: string }): Promise<ReadStatusResponse> {
    const url = withQuery("/fix-actions/read-status", { brandId: args.brandId });
    return apiFetch<ReadStatusResponse>(url);
  },

  async updateEventStatus(req: UpdateEventStatusRequest): Promise<{
    ok: boolean;
    actionId: number;
    eventStatus: ActionEventStatus;
    generatedCount: number;
    closedCount: number;
    totalArtifacts: number;
  }> {
    return apiFetch("/fix-actions/event-status/update", jsonOptions("POST", req));
  },

  async getEventStatuses(args: { brandId: string }): Promise<EventStatusResponse> {
    const url = withQuery("/fix-actions/event-status", { brandId: args.brandId });
    return apiFetch<EventStatusResponse>(url);
  },

  /** #133: the artifact as a Brand Publisher draft. It reads Published only
   *  once a publication record carries it (a publish from Brand Publisher). */
  async sendArtifactToPublisher(args: {
    artifactId: string;
    brandId: string;
    brandName: string;
    destinations?: string[];
  }): Promise<{ ok: boolean; publishing_draft_id: number }> {
    const url = withQuery(
      `/fix-actions/assets/${encodeURIComponent(args.artifactId)}/send-to-publisher`,
      { brandId: args.brandId }
    );
    return apiFetch(url, jsonOptions("POST", {
      brand_name: args.brandName,
      destinations: args.destinations ?? ["wordpress"],
    }));
  },

  async updateArtifactStatus(args: {
    artifactId: string;
    status: string;
    brandId: string;
    brandName?: string;
  }): Promise<{ ok: boolean; artifactId: string; status: string }> {
    const url = withQuery(
      `/fix-actions/assets/${encodeURIComponent(args.artifactId)}/status`,
      { brandId: args.brandId, brandName: args.brandName }
    );
    return apiFetch(url, jsonOptions("PATCH", { status: args.status }));
  },
};